import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@/components/ui';
import { formatDate, formatTime } from '@/lib/utils/date';
import type { UseBooking } from '../booking/useBooking';

interface FirstAvailableResultProps {
  booking: UseBooking;
}

/** First-available search trigger + result/searching/empty states. */
export function FirstAvailableResult({ booking }: FirstAvailableResultProps) {
  const { t } = useTranslation();
  const { firstAvailable, firstAvailableActive } = booking;

  if (firstAvailable.status === 'idle') {
    return (
      <Button onClick={() => void firstAvailable.search()} disabled={!firstAvailableActive} block>
        {t('booking.search_first')}
      </Button>
    );
  }

  if (firstAvailable.status === 'searching') {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-ink-mute">
        <Spinner size={18} /> {t('booking.searching_first')}
      </div>
    );
  }

  if (firstAvailable.status === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-input bg-warning-light px-4 py-5 text-center">
        <p className="text-sm font-semibold text-[#9a6700]">{firstAvailable.error}</p>
        <Button variant="outline" size="sm" onClick={() => void firstAvailable.search()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const slot = firstAvailable.slot;
  if (!slot) return null;

  return (
    <div className="flex flex-col gap-3 rounded-input border border-primary/30 bg-primary-light/50 p-4">
      <div className="flex items-center gap-2 text-primary">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-sm font-bold">{t('booking.first_found')}</span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-ink-mute">{t('booking.summary_doctor')}</dt>
        <dd className="font-semibold text-ink">{slot.doctorName}</dd>
        <dt className="text-ink-mute">{t('booking.summary_service')}</dt>
        <dd className="text-ink">{slot.serviceName}</dd>
        <dt className="text-ink-mute">{t('booking.summary_datetime')}</dt>
        <dd className="font-semibold text-ink">
          {formatDate(slot.day)} · {formatTime(slot.start)}
        </dd>
      </dl>
    </div>
  );
}
